/**
 * @fileoverview Translate file metadata into a shared document-type signifier. `documentFilePolicy.js` owns format classification and `kc-internal-frontend/src/components/shared/AppIcon.js` owns managed icons. The display choice helps users identify content without proving that the file can be opened, edited, or accessed.
 */

import React from 'react';
import AppIcon from '../shared/AppIcon';
import { WORD_DOCUMENT_EXTENSIONS } from './documentFilePolicy';

const FILE_TYPE_DEFINITIONS = {
  word: { icon: 'pi pi-file-word', label: 'Word document' },
  pdf: { icon: 'pi pi-file-pdf', label: 'PDF' },
  spreadsheet: { icon: 'pi pi-file-excel', label: 'Spreadsheet' },
  csv: { icon: 'pi pi-file-csv', label: 'CSV file' },
  image: { icon: 'pi pi-file-image', label: 'Image' },
  presentation: { icon: 'pi pi-file-powerpoint', label: 'Presentation' },
  audio: { icon: 'pi pi-file-audio', label: 'Audio file' },
  video: { icon: 'pi pi-file-video', label: 'Video file' },
  archive: { icon: 'pi pi-file-archive', label: 'Archive' },
  code: { icon: 'pi pi-file-code', label: 'Code file' },
  text: { icon: 'pi pi-file', label: 'Text file' },
  file: { icon: 'pi pi-file', label: 'File' },
};

const WORD_EXTENSIONS = new Set(WORD_DOCUMENT_EXTENSIONS);
const SPREADSHEET_EXTENSIONS = new Set(['ods', 'xls', 'xlsb', 'xlsm', 'xlsx', 'xlt', 'xltm', 'xltx']);
const IMAGE_EXTENSIONS = new Set(['avif', 'bmp', 'gif', 'heic', 'heif', 'ico', 'jpeg', 'jpg', 'png', 'svg', 'tif', 'tiff', 'webp']);
const PRESENTATION_EXTENSIONS = new Set(['odp', 'pot', 'potm', 'potx', 'pps', 'ppsm', 'ppsx', 'ppt', 'pptm', 'pptx']);
const AUDIO_EXTENSIONS = new Set(['aac', 'aiff', 'flac', 'm4a', 'mp3', 'oga', 'ogg', 'opus', 'wav', 'wma']);
const VIDEO_EXTENSIONS = new Set(['avi', 'm4v', 'mkv', 'mov', 'mp4', 'mpeg', 'mpg', 'ogv', 'webm', 'wmv']);
const ARCHIVE_EXTENSIONS = new Set(['7z', 'bz', 'bz2', 'gz', 'rar', 'tar', 'tgz', 'xz', 'zip']);
const CODE_EXTENSIONS = new Set(['css', 'graphql', 'htm', 'html', 'ini', 'js', 'json', 'jsx', 'md', 'sql', 'toml', 'ts', 'tsx', 'xml', 'yaml', 'yml']);
const TEXT_EXTENSIONS = new Set(['log', 'rtf', 'text', 'txt']);



function fileExtension(file) {
  const filename = typeof file === 'string'
    ? file
    : file?.filename || file?.name || file?.title || '';
  const basename = String(filename).split(/[\\/]/).pop() || '';
  if (basename.includes('.') && !basename.endsWith('.')) {
    return basename.split('.').pop().toLowerCase();
  }
  const explicit = typeof file === 'object'
    ? file?.extension || file?.file_manager?.extension
    : '';
  return String(explicit || '').trim().replace(/^\./, '').toLowerCase();
}



function fileMimeType(file) {
  if (!file || typeof file === 'string') return '';
  return String(
    file.mime_type ||
    file.latest_mime_type ||
    file.mimeType ||
    file.content_type ||
    file.file_manager?.mime_type ||
    file.type ||
    ''
  ).toLowerCase();
}



function hasDocxSignal(file) {
  if (!file || typeof file === 'string') return false;
  return Boolean(
    file.is_docx === true ||
    file.file_manager?.is_docx === true ||
    file.latest_version?.is_docx === true ||
    file.latest_revision?.is_docx === true
  );
}



export function documentFileType(file) {
  const extension = fileExtension(file);
  const mimeType = fileMimeType(file);

  if (hasDocxSignal(file) || WORD_EXTENSIONS.has(extension) || mimeType.includes('msword') || mimeType.includes('wordprocessingml')) return 'word';
  if (extension === 'pdf' || mimeType === 'application/pdf') return 'pdf';
  if (extension === 'csv' || extension === 'tsv' || mimeType.includes('csv') || mimeType.includes('tab-separated')) return 'csv';
  if (SPREADSHEET_EXTENSIONS.has(extension) || mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'spreadsheet';
  if (IMAGE_EXTENSIONS.has(extension) || mimeType.startsWith('image/')) return 'image';
  if (PRESENTATION_EXTENSIONS.has(extension) || mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'presentation';
  if (AUDIO_EXTENSIONS.has(extension) || mimeType.startsWith('audio/')) return 'audio';
  if (VIDEO_EXTENSIONS.has(extension) || mimeType.startsWith('video/')) return 'video';
  if (ARCHIVE_EXTENSIONS.has(extension) || /(zip|compressed|archive|gzip|rar|7z)/.test(mimeType)) return 'archive';
  if (CODE_EXTENSIONS.has(extension) || /(javascript|json|xml|yaml)/.test(mimeType)) return 'code';
  if (TEXT_EXTENSIONS.has(extension) || mimeType.startsWith('text/')) return 'text';
  return 'file';
}



export function documentTypeIcon(file) {
  return FILE_TYPE_DEFINITIONS[documentFileType(file)].icon;
}



export function documentTypeLabel(file) {
  return FILE_TYPE_DEFINITIONS[documentFileType(file)].label;
}



function DocumentFileTypeIcon({ file, className = '', ...props }) {
  const type = documentFileType(file);
  return (
    <AppIcon
      icon={FILE_TYPE_DEFINITIONS[type].icon}
      className={`document-file-type-icon document-file-type-icon--${type} ${className}`.trim()}
      data-file-type={type}
      {...props} />
  );
}

export default DocumentFileTypeIcon;
